import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ─────────────────────────────────────────────────────────────────────
   Registration requests are saved into a Google Sheet via a Google Form.
   👉 Create a Google Form with the fields below (in this order), then
   paste its `formResponse` URL and each question's `entry.XXXX` id here.
   In the linked response Sheet, add an "Approval / Reject" column with a
   data-validation dropdown.
──────────────────────────────────────────────────────────────────────*/
const REG_FORM = {
  url: "https://docs.google.com/forms/d/e/1FAIpQLScPj8FGWqQPdJkLuSXMLFHnxTm9JT_LI48ioHs0mpAeZxovEg/formResponse",
  entries: {
    partyName: "entry.555086582",
    gstin: "entry.678877658",
    district: "entry.877677000",
    taluka: "entry.1597346478",
    area: "entry.1768756891",
    contactPerson: "entry.1764051155",
    contactNo: "entry.710362458",
    email: "entry.1564598098",
    type: "entry.1895555529",
    remarks: "entry.403108960",
  },
};

const TYPE_OPTIONS = [
  "Dealer",
  "Builder",
  "Contractor",
  "Fabricator",
  "Industrial",
  "End User",
  "Project",
];

const GST_CHECK_URL = "https://services.gst.gov.in/services/searchtp";

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefillContact = location.state?.contactNo || "";

  const [form, setForm] = useState({
    partyName: "",
    gstin: "",
    district: "",
    taluka: "",
    area: "",
    contactPerson: "",
    contactNo: prefillContact,
    email: "",
    type: "",
    remarks: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitToSheet = async () => {
    const fd = new FormData();
    Object.entries(REG_FORM.entries).forEach(([key, entry]) => {
      fd.append(entry, form[key] || "");
    });
    await fetch(REG_FORM.url, { method: "POST", body: fd, mode: "no-cors" });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;

    // All fields required EXCEPT Email Id and Remarks.
    const requiredFields = [
      ["partyName", "Party Name"],
      ["gstin", "GSTIN/UIN"],
      ["district", "District"],
      ["taluka", "Taluka"],
      ["area", "Area"],
      ["contactPerson", "Contact Person Name"],
      ["contactNo", "Contact No"],
    ];
    for (const [k, label] of requiredFields) {
      if (!String(form[k] || "").trim()) {
        toast.error(`${label} is required.`);
        return;
      }
    }
    if (!form.type) {
      toast.error("Please select a Type.");
      return;
    }

    // ── GSTIN — must be a valid 15-character Indian GSTIN
    const gst = form.gstin.trim().toUpperCase();
    const GSTIN_RE =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!GSTIN_RE.test(gst)) {
      toast.error("Enter a valid 15-digit GSTIN (e.g. 24ABCDE1234F1Z5).");
      return;
    }

    // ── Contact No — exactly 10 digits, starting 6-9
    if (!/^[6-9]\d{9}$/.test(form.contactNo)) {
      toast.error("Contact No must be a valid 10-digit mobile number.");
      return;
    }

    // ── Party / Person / District / Taluka / Area — at least 2 letters
    const nameChecks = [
      ["partyName", "Party Name"],
      ["contactPerson", "Contact Person Name"],
      ["district", "District"],
      ["taluka", "Taluka"],
      ["area", "Area"],
    ];
    for (const [k, label] of nameChecks) {
      const v = String(form[k] || "").trim();
      if (/[0-9]/.test(v)) {
        toast.error(`${label} should not contain numbers.`);
        return;
      }
      if (!/[A-Za-z]/.test(v) || v.length < 2) {
        toast.error(`Enter a valid ${label}.`);
        return;
      }
    }

    // ── Email Id — optional, but if filled must be a valid email
    if (
      form.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())
    ) {
      toast.error("Enter a valid Email Id.");
      return;
    }

    setBusy(true);
    try {
      if (REG_FORM.url.includes("PASTE_FORM_ID")) {
        toast.error("Registration form is not connected yet. Please contact admin.");
        setBusy(false);
        return;
      }
      await submitToSheet();
      toast.success("✅ Registration request submitted for approval!");
      setTimeout(() => navigate("/"), 1500);
    } catch (err) {
      toast.error("Submit failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // NOTE: a plain function (NOT an inline <Component/>) so inputs don't
  // remount and lose focus on every keystroke.
  const field = (label, k, opts = {}) => (
    <div>
      <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
        {label}
        {opts.required && <span className="text-red-500"> *</span>}
      </label>
      <input
        value={form[k]}
        onChange={(e) => {
          let v = e.target.value;
          if (opts.onlyDigits) v = v.replace(/\D/g, ""); // mobile → digits only
          else if (opts.noDigits) v = v.replace(/[0-9]/g, ""); // names → no numbers
          set(k, v);
        }}
        placeholder={opts.placeholder || ""}
        inputMode={opts.inputMode}
        maxLength={opts.maxLength}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
      />
    </div>
  );

  return (
    <>
      <div
        className="fixed inset-0 bg-cover bg-center blur-sm z-[-1]"
        style={{ backgroundImage: "url('/images/bg_friends.jpeg')" }}
      />
      <div className="fixed inset-0 bg-white/25 backdrop-blur-sm z-[-1]" />

      <div className="min-h-screen flex items-center justify-center py-6 px-3">
        <form
          onSubmit={handleSubmit}
          className="bg-white w-full max-w-4xl p-5 sm:p-8 rounded-2xl shadow-xl"
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <img
              src="/images/Logo_Thumbnail-removebg.png"
              alt="Logo"
              className="h-14"
            />
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
              Sign Up
            </h2>
          </div>

          {/* Multi-column grid → fits on one desktop screen (no scroll) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-3">
            {field("Party Name", "partyName", { required: true, noDigits: true })}

            {/* GSTIN with already-registered check */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                GSTIN/UIN <span className="text-red-500">*</span>
              </label>
              <input
                value={form.gstin}
                onChange={(e) =>
                  set("gstin", e.target.value.toUpperCase().replace(/\s/g, ""))
                }
                maxLength={15}
                placeholder="24ABCDE1234F1Z5"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-gray-800"
              />
              <p className="text-[11px] text-gray-500 mt-1 text-left">
                Check GST —{" "}
                <a
                  href={GST_CHECK_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  GST Search
                </a>
              </p>
            </div>

            {field("District", "district", { required: true, noDigits: true })}
            {field("Taluka", "taluka", { required: true, noDigits: true })}
            {field("Area", "area", { required: true, noDigits: true })}
            {field("Contact Person Name", "contactPerson", {
              required: true,
              noDigits: true,
            })}
            {field("Contact No", "contactNo", {
              required: true,
              inputMode: "numeric",
              onlyDigits: true,
              maxLength: 10,
              placeholder: "10-digit number",
            })}
            {field("Email Id", "email", { placeholder: "optional" })}

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                value={form.type}
                onChange={(e) => set("type", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-800"
              >
                <option value="">-- Select Type --</option>
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {field("Remarks", "remarks", { placeholder: "e.g. Marketing" })}
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full mt-5 bg-gray-800 text-white font-bold py-3 rounded-xl hover:bg-gray-900 disabled:opacity-50 transition"
          >
            {busy ? "Submitting…" : "Submit Request"}
          </button>

          <p className="mt-4 text-sm text-center">
            Already registered?{" "}
            <Link to="/" className="text-blue-600 hover:underline font-semibold">
              Login
            </Link>
          </p>
        </form>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={4000}
        hideProgressBar
        newestOnTop
        closeOnClick
        theme="colored"
      />
    </>
  );
}
